import { useRef, useMemo, useState, useEffect } from "react"
import { useFrame } from "@react-three/fiber";
import { useTheme } from "../../contexts/ThemeContext";
import { lightingConfig } from "../../model_data/lights";
import { lerp, lerpColor } from "../../utils/themeTransition";
import { useDimension } from "../../contexts/DimensionalContext";

export default function Lights({ onTransitionProgress }){
    const light = useRef();
    const { theme } = useTheme();
    const ambientLightRef = useRef();
    const fillLightRef = useRef();
    const { portfolioDimension } = useDimension();

    const [transitionProgress, setTransitionProgress] = useState(theme === 'dark' ? 0 : 1);

    // Point Lights
    const staticLights = useMemo(() => {
        const getPointLightIntensity = () => theme === "dark" ? 2 : 0;
        const getPointLightDistance = () => theme === "dark" ? 10 : 0;
        
        return [
            { color: "pink", intensity: 10, distance: 10, position: [0.5, -3, -4.8] },
            { color: "white", intensity: 3, distance: 10, position: [-3.5, -5, 5.9] },
            { color: "white", intensity: getPointLightIntensity(), distance: getPointLightDistance(), position: [-3.8, -5.3, 13.8] },
            { color: "white", intensity: getPointLightIntensity(), distance: getPointLightDistance(), position: [1.5, -5.2, 16] },
            { color: "white", intensity: getPointLightIntensity(), distance: getPointLightDistance(), position: [-4.8, -5.3, 20.5] },
            { color: "white", intensity: getPointLightIntensity(), distance: getPointLightDistance(), position: [-11.9, -5.8, 14.2] },
            { color: "white", intensity: getPointLightIntensity(), distance: getPointLightDistance(), position: [-17.9, -5.8, 20.2] }
        ];
    }, [theme]);

    const staticLights2D = useMemo(() => {
        const getPointLightIntensity = () => theme === "dark" ? 4 : 0;
        const getPointLightDistance = () => theme === "dark" ? 10 : 0;
        
        return [
            { color: "white", intensity: getPointLightIntensity(), distance: getPointLightDistance(), position: [-17.58, -2.26, -10.23] },
            { color: "white", intensity: getPointLightIntensity(), distance: getPointLightDistance(), position: [-13.58, -2.26, -14.23] },
            { color: "white", intensity: getPointLightIntensity(), distance: getPointLightDistance(), position: [-19.58, -2.26, -18.23] },
            { color: "white", intensity: getPointLightIntensity(), distance: getPointLightDistance(), position: [-12.58, -2.26, -20.23] }
        ];
    }, [ theme ])

    // Transition between night / day
    useEffect(() => {
        const transitinoDuration = 2000;
        const startTime = Date.now();
        const startProgress = transitionProgress;
        const targetProgress = theme === 'dark' ? 0 : 1;

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / transitinoDuration, 1);

            const eased = progress * progress * (3 - 2 * progress);

            const newProgress = lerp(startProgress, targetProgress, eased);
            setTransitionProgress(newProgress);
            
            // Pass the progress to parent component
            if (onTransitionProgress) {
                onTransitionProgress(newProgress);
            }

            if(progress < 1){
                requestAnimationFrame(animate);
            }
        }

        animate();
    }, [theme, onTransitionProgress]);

    // main directional light update
    useFrame((state) => {
        if (light.current) {
            const cameraZ = state.camera.position.z;
            light.current.position.z = cameraZ + 1 - 4;
            light.current.target.position.z = cameraZ - 4;
            light.current.target.updateMatrixWorld();
            
            // Interpolate lighting properties
            const darkConfig = lightingConfig.dark;
            const lightConfig = lightingConfig.light;
            
            // Interpolate intensity
            light.current.intensity = lerp(
                darkConfig.directional.intensity,
                lightConfig.directional.intensity,
                transitionProgress
            );
            
            // Interpolate color
            const currentColor = lerpColor(
                darkConfig.directional.color,
                lightConfig.directional.color,
                transitionProgress
            );
            light.current.color = currentColor;
            
            // Interpolate Y position for sun arc
            light.current.position.y = lerp(
                darkConfig.directional.position[1],
                lightConfig.directional.position[1],
                transitionProgress
            );
        }
        
        // Update ambient light
        if (ambientLightRef.current) {
            ambientLightRef.current.intensity = lerp(
                lightingConfig.dark.ambient.intensity,
                lightingConfig.light.ambient.intensity,
                transitionProgress
            );
            
            const ambientColor = lerpColor(
                lightingConfig.dark.ambient.color,
                lightingConfig.light.ambient.color,
                transitionProgress
            );
            ambientLightRef.current.color = ambientColor;
        }
        
        // Update fill light
        if (fillLightRef.current) {
            fillLightRef.current.intensity = lerp(
                lightingConfig.dark.fill.intensity,
                lightingConfig.light.fill.intensity,
                transitionProgress
            );
            
            const fillColor = lerpColor(
                lightingConfig.dark.fill.color,
                lightingConfig.light.fill.color,
                transitionProgress
            );
            fillLightRef.current.color = fillColor;
        }
    });

    return <>
        {/* Main directional light */}
        <directionalLight
            ref={light}
            castShadow
            position={portfolioDimension ? [-45, 30, 20] : [-45, 30, 20]}
            shadow-mapSize={[4048, 4048]} 
            shadow-camera-near={0.5}
            shadow-camera-far={50}
            shadow-camera-top={15}
            shadow-camera-right={15}
            shadow-camera-bottom={-15}
            shadow-camera-left={-15}
            shadow-bias={-0.0001}
        />
        
        <ambientLight
            ref={ambientLightRef}
            intensity={0.1}
            color="#1a1a2e"
        />
        
        {/* Fill light for daytime */}
        <directionalLight
            ref={fillLightRef}
            intensity={0}
            color="#000000"
            position={[45, 20, -20]}
        />
        
        {(portfolioDimension ? staticLights : staticLights2D).map((lightConfig, index) => (
            <pointLight
                key={index}
                color={lightConfig.color}
                intensity={lightConfig.intensity}
                distance={lightConfig.distance * 0.8}
                position={lightConfig.position}
                decay={2}
            />
        ))}
    </>
}